import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, InputNumber, Input, Typography, Row, Col, Button, App, Segmented } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { CashRegisterDto } from '../../types/cashRegister';
import { printAgentService } from '../../services/printAgentService';
import { formatTicketMoney, withPrintAnimation, PrintTicketPreview } from '../../services/printAnimation';
import { bluetoothPrintService } from '../../services/bluetoothPrintService';
import { useGetRestaurantSettingsQuery } from '../../services/restaurantsApi';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import { useCloudPrint } from '../../hooks/useCloudPrint';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;

// Mexican peso denominations
const BILL_DENOMINATIONS = [
  { value: 1000, label: '$1,000' },
  { value: 500, label: '$500' },
  { value: 200, label: '$200' },
  { value: 100, label: '$100' },
  { value: 50, label: '$50' },
  { value: 20, label: '$20' },
];

const COIN_DENOMINATIONS = [
  { value: 20, label: '$20' },
  { value: 10, label: '$10' },
  { value: 5, label: '$5' },
  { value: 2, label: '$2' },
  { value: 1, label: '$1' },
  { value: 0.5, label: '$0.50' },
];

interface DenominationCount {
  [key: number]: number;
}

interface CloseCashRegisterModalProps {
  visible: boolean;
  cashRegister: CashRegisterDto | null;
  onCancel: () => void;
  onConfirm: (finalAmount: number, notes?: string) => void;
  loading: boolean;
}

const CloseCashRegisterModal: React.FC<CloseCashRegisterModalProps> = ({
  visible,
  cashRegister,
  onCancel,
  onConfirm,
  loading,
}) => {
  const { message } = App.useApp();
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [form] = Form.useForm();
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();
  const [finalAmount, setFinalAmount] = useState<number>(0);
  const [variance, setVariance] = useState<number>(0);
  const [printAgentAvailable, setPrintAgentAvailable] = React.useState(false);
  const [bluetoothAvailable, setBluetoothAvailable] = React.useState(false);
  const [printing, setPrinting] = React.useState(false);

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  const cloudPrintEnabled = cloudPrintSetting?.value ?? false;
  const { printJob: cloudPrintJob } = useCloudPrint();
  const [useDetailedCount, setUseDetailedCount] = useState<boolean>(false);
  const [billCounts, setBillCounts] = useState<DenominationCount>({});
  const [coinCounts, setCoinCounts] = useState<DenominationCount>({});
  // Desglose de ventas: contexto secundario, colapsado por defecto
  const [showBreakdown, setShowBreakdown] = useState<boolean>(false);

  const moneyStyle: React.CSSProperties = {
    fontFamily: 'ui-monospace, Consolas, monospace',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  };

  // Calculate derived values with null-safe operations
  const totalMovements = cashRegister?.summary?.totalMovements ?? cashRegister?.summary?.totalTransactions ?? 0;
  // Calculate total from detailed count
  const detailedTotal = useMemo(() => {
    let total = 0;
    BILL_DENOMINATIONS.forEach(d => {
      total += (billCounts[d.value] || 0) * d.value;
    });
    COIN_DENOMINATIONS.forEach(d => {
      total += (coinCounts[d.value] || 0) * d.value;
    });
    return total;
  }, [billCounts, coinCounts]);

  useEffect(() => {
    if (cashRegister && visible) {
      const expected = cashRegister.expectedAmount;
      setFinalAmount(expected);
      form.setFieldsValue({ finalAmount: expected });
      // Reset detailed count when modal opens
      setBillCounts({});
      setCoinCounts({});
      setUseDetailedCount(false);
    }
  }, [cashRegister, visible, form]);

  // Sync detailed count total with final amount
  useEffect(() => {
    if (useDetailedCount) {
      setFinalAmount(detailedTotal);
      form.setFieldsValue({ finalAmount: detailedTotal });
    }
  }, [detailedTotal, useDetailedCount, form]);

  useEffect(() => {
    if (cashRegister) {
      const calculatedVariance = finalAmount - cashRegister.expectedAmount;
      setVariance(calculatedVariance);
    }
  }, [finalAmount, cashRegister]);

  const handleBillCountChange = (denomination: number, count: number | null) => {
    setBillCounts(prev => ({ ...prev, [denomination]: count || 0 }));
  };

  const handleCoinCountChange = (denomination: number, count: number | null) => {
    setCoinCounts(prev => ({ ...prev, [denomination]: count || 0 }));
  };

  const handleModeChange = (checked: boolean) => {
    setUseDetailedCount(checked);
    if (!checked) {
      // When switching back to direct input, keep the calculated total
      // but allow manual editing
    }
  };

  // Check for available print services when modal opens
  React.useEffect(() => {
    const checkPrintServices = async () => {
      setBluetoothAvailable(bluetoothPrintService.isAvailable());
      setPrintAgentAvailable(await printAgentService.isAvailable());
    };
    if (visible) {
      checkPrintServices();
    }
  }, [visible]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      // First, confirm the cash register closure
      onConfirm(values.finalAmount, values.notes);

      // Then, automatically print the ticket
      if (cashRegister) {
        await handlePrint();
      }
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  // Unified print function with priority-based service selection
  const handlePrint = async () => {
    if (!cashRegister) return;

    // Comensales atendidos por esta caja (los calcula el backend sobre las
    // órdenes cobradas aquí, incluidas las ya cerradas).
    const totalAdults = cashRegister.summary?.totalAdults ?? 0;
    const totalChildren = cashRegister.summary?.totalChildren ?? 0;

    const summary = cashRegister.summary;
    const preview: PrintTicketPreview = {
      kind: 'corte',
      title: 'CORTE DE CAJA',
      sections: [
        [
          { left: 'Cajero', right: cashRegister.userName || '' },
          { left: 'Abierta', right: formatInTimeZone(cashRegister.openTime, 'DD/MM HH:mm') },
        ],
        [
          { left: 'Monto inicial', right: formatTicketMoney(cashRegister.initialAmount || 0) },
          { left: 'Ventas', right: formatTicketMoney(summary?.totalSales || 0) },
          { left: 'Propinas', right: formatTicketMoney(summary?.totalTips || 0) },
        ],
        [
          { left: 'T. Crédito', right: formatTicketMoney(summary?.totalCreditCardSales ?? 0) },
          { left: 'T. Débito', right: formatTicketMoney(summary?.totalDebitCardSales ?? 0) },
          { left: 'Transferencia', right: formatTicketMoney(summary?.totalTransferSales ?? 0) },
          { left: 'Otros', right: formatTicketMoney(summary?.totalOtherSales ?? 0) },
          { left: 'Comensales', right: String(totalAdults + totalChildren) },
        ],
        [
          { left: 'Esperado', right: formatTicketMoney(cashRegister.expectedAmount || 0) },
          { left: 'Monto final', right: formatTicketMoney(finalAmount), strong: true },
          { left: 'Diferencia', right: formatTicketMoney(variance) },
        ],
      ],
    };

    setPrinting(true);
    try {
      // Try Cloud Print first (if enabled)
      if (cloudPrintEnabled) {
        // Field names must match the backend EscPosReceiptData DTO exactly
        // (JSON binding is case-insensitive but does not translate names).
        // GenerateCashRegisterClosureReceipt only reads RestaurantName/WaiterName/DateTime/Footer.
        const notes = form.getFieldValue('notes');
        const footer = `Abierta: ${formatInTimeZone(cashRegister.openTime, 'DD/MM/YYYY HH:mm')}\n`
          + `Cerrada: ${formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm:ss')}\n\n`
          + `Monto Inicial: ${currencySymbol}${(cashRegister.initialAmount || 0).toFixed(2)}\n`
          + `Total Ventas: ${currencySymbol}${(cashRegister.summary?.totalSales || 0).toFixed(2)}\n`
          + `Total Propinas: ${currencySymbol}${(cashRegister.summary?.totalTips || 0).toFixed(2)}\n\n`
          + `--- Ventas por Metodo ---\n`
          + `Efectivo: ${currencySymbol}${(cashRegister.summary?.totalSales || 0).toFixed(2)}\n`
          + `T. Credito: ${currencySymbol}${(cashRegister.summary?.totalCreditCardSales ?? 0).toFixed(2)}\n`
          + `T. Debito: ${currencySymbol}${(cashRegister.summary?.totalDebitCardSales ?? 0).toFixed(2)}\n`
          + `Transferencia: ${currencySymbol}${(cashRegister.summary?.totalTransferSales ?? 0).toFixed(2)}\n`
          + `Otros: ${currencySymbol}${(cashRegister.summary?.totalOtherSales ?? 0).toFixed(2)}\n\n`
          + `--- Comensales ---\n`
          + `Adultos: ${totalAdults}\n`
          + `Niños: ${totalChildren}\n`
          + `Total: ${totalAdults + totalChildren}\n\n`
          + `Monto Esperado: ${currencySymbol}${(cashRegister.expectedAmount || 0).toFixed(2)}\n`
          + `Monto Final: ${currencySymbol}${finalAmount.toFixed(2)}\n`
          + `Diferencia: ${currencySymbol}${variance.toFixed(2)}`
          + (notes ? `\n\nNotas: ${notes}` : '');

        await withPrintAnimation(preview, () => cloudPrintJob({
          printType: 'CashRegisterClosure',
          receiptData: {
            restaurantName: restaurantSettings?.name || 'MASSRESTAURANT',
            title: 'CIERRE DE CAJA',
            tableName: 'CAJA',
            waiterName: cashRegister.userName || '',
            total: finalAmount,
            footer,
          },
        }));
        message.success('Cierre de caja impreso exitosamente');
      }
      // Try Print Agent (Windows)
      else if (printAgentAvailable) {
        const result = await withPrintAnimation(preview, () => printAgentService.printCashRegisterClosure(
          restaurantSettings?.name || 'MASSRESTAURANT',
          cashRegister.userName || '',
          formatInTimeZone(cashRegister.openTime, 'DD/MM/YYYY HH:mm'),
          cashRegister.initialAmount || 0,
          cashRegister.summary?.totalSales || 0,
          cashRegister.summary?.totalTips || 0,
          cashRegister.expectedAmount || 0,
          finalAmount,
          variance,
          cashRegister.summary?.totalCreditCardSales ?? 0,
          cashRegister.summary?.totalDebitCardSales ?? 0,
          cashRegister.summary?.totalTransferSales ?? 0,
          cashRegister.summary?.totalOtherSales ?? 0,
          totalAdults,
          totalChildren,
          form.getFieldValue('notes')
        ));

        if (result.success) {
          message.success('Cierre de caja impreso exitosamente');
        } else {
          message.error(`Error al imprimir: ${result.message}`);
          console.error('Print Agent error:', result.error);
        }
      }
      // Try Bluetooth if Print Agent not available (Android/Chrome)
      else if (bluetoothAvailable) {
        const result = await withPrintAnimation(preview, () => bluetoothPrintService.printCashRegisterClosure(
          restaurantSettings?.name || 'MASSRESTAURANT',
          cashRegister.userName || '',
          formatInTimeZone(cashRegister.openTime, 'DD/MM/YYYY HH:mm'),
          cashRegister.initialAmount || 0,
          cashRegister.summary?.totalSales || 0,
          cashRegister.summary?.totalTips || 0,
          cashRegister.expectedAmount || 0,
          finalAmount,
          variance,
          cashRegister.summary?.totalCreditCardSales ?? 0,
          cashRegister.summary?.totalDebitCardSales ?? 0,
          cashRegister.summary?.totalTransferSales ?? 0,
          cashRegister.summary?.totalOtherSales ?? 0,
          totalAdults,
          totalChildren,
          form.getFieldValue('notes')
        ));

        if (result.success) {
          message.success('Cierre de caja impreso exitosamente');
        } else {
          message.error(`Error al imprimir: ${result.message}`);
          console.error('Bluetooth error:', result.error);
        }
      }
      // No print service available
      else {
        message.warning('No hay servicio de impresión disponible');
        console.warn('No print service available');
      }
    } catch (error: any) {
      console.error('Print error:', error);
      message.error('Error al imprimir el cierre de caja');
    } finally {
      setPrinting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFinalAmount(0);
    setVariance(0);
    onCancel();
  };

  // Mismos umbrales de siempre (<1 exacta, <10 menor, ≥10 significativa),
  // ahora con tokens del tema en lugar de hex hardcodeados.
  const getVarianceColor = (v: number) => {
    if (Math.abs(v) < 1) return 'var(--color-success-text)';
    if (Math.abs(v) < 10) return 'var(--color-warning-text)';
    return 'var(--color-error-text)';
  };

  const getVarianceMessage = (v: number) => {
    if (Math.abs(v) < 1) return 'exacta';
    if (v > 0) return 'sobrante';
    return 'faltante';
  };

  const hasVariance = Math.abs(variance) >= 1;
  const varianceLabel = `${variance > 0 ? '+' : '−'}${currencySymbol}${Math.abs(variance).toFixed(2)}`;

  const getOperatingTime = () => {
    if (!cashRegister) return '';
    const openTime = dayjs(cashRegister.openTime);
    const now = dayjs();
    const duration = now.diff(openTime, 'minute');
    
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  if (!cashRegister) return null;

  return (
    <>
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', paddingRight: 28 }}>
          <span>Cerrar caja</span>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {cashRegister.userName} · abierta {formatInTimeZone(cashRegister.openTime, 'HH:mm')} · operando {getOperatingTime()}
          </Text>
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      confirmLoading={loading}
      width={isMobile ? '95%' : 600}
      centered
      destroyOnClose
      footer={
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 8,
          width: '100%',
        }}>
          <Button
            key="print"
            type="text"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            loading={printing}
            block={isMobile}
            style={{ marginRight: isMobile ? undefined : 'auto' }}
          >
            Imprimir
          </Button>
          <div style={{
            display: 'flex',
            gap: 8,
            justifyContent: 'flex-end',
          }}>
            <Button
              key="cancel"
              onClick={handleCancel}
              style={{ flex: isMobile ? 1 : undefined }}
            >
              Cancelar
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={handleSubmit}
              danger={hasVariance}
              style={{ flex: isMobile ? 1 : undefined }}
            >
              {/* El botón repite la diferencia: se firma lo que se está aceptando */}
              {hasVariance ? `Cerrar caja con ${varianceLabel}` : 'Cerrar caja'}
            </Button>
          </div>
        </div>
      }
    >
      <div style={{ padding: isMobile ? '4px 0' : '8px 0' }}>
        {/* Hero: la diferencia ES el dato del cierre, en vivo */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: isMobile ? 10 : 18,
            flexWrap: 'wrap',
            border: '1px solid var(--border-color)',
            borderRadius: 9,
            background: 'var(--bg-secondary)',
            padding: '10px 14px',
            marginBottom: 14,
          }}
        >
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Efectivo esperado</Text>
            <span style={{ ...moneyStyle, fontSize: 19 }}>
              {currencySymbol}{(cashRegister.expectedAmount ?? 0).toFixed(2)}
            </span>
          </div>
          {!isMobile && <span style={{ color: 'var(--text-secondary)', fontSize: 17 }}>→</span>}
          <div>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Contado</Text>
            <span style={{ ...moneyStyle, fontSize: 19 }}>{currencySymbol}{finalAmount.toFixed(2)}</span>
          </div>
          <div style={{ textAlign: 'right' }}>
            <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Diferencia</Text>
            <span style={{ ...moneyStyle, fontSize: 19, color: getVarianceColor(variance) }}>
              {variance > 0 ? '+' : variance < 0 ? '−' : ''}{currencySymbol}{Math.abs(variance).toFixed(2)}
            </span>
            <Text style={{ display: 'block', fontSize: 11, color: getVarianceColor(variance) }}>
              {getVarianceMessage(variance)}
            </Text>
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
        >
          {/* Modo como Segmented, no Switch con iconitos */}
          <Segmented
            value={useDetailedCount ? 'detailed' : 'direct'}
            onChange={(value) => handleModeChange(value === 'detailed')}
            options={[
              { label: 'Ingresar total', value: 'direct' },
              { label: 'Contar billetes y monedas', value: 'detailed' },
            ]}
            style={{ marginBottom: 14 }}
          />

          {useDetailedCount ? (
            <div style={{ marginBottom: 14 }}>
              {/* Denominaciones en grid neutral: denominación × cantidad = subtotal */}
              {[
                { title: 'Billetes', denoms: BILL_DENOMINATIONS, counts: billCounts, onChange: handleBillCountChange },
                { title: 'Monedas', denoms: COIN_DENOMINATIONS, counts: coinCounts, onChange: handleCoinCountChange },
              ].map((group) => (
                <div key={group.title} style={{ marginBottom: 10 }}>
                  <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 4 }}>{group.title}</Text>
                  <Row gutter={[8, 8]}>
                    {group.denoms.map(denom => (
                      <Col xs={12} sm={8} key={`${group.title}-${denom.value}`}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Text style={{ ...moneyStyle, minWidth: 48, fontSize: isMobile ? 12 : 13, fontWeight: 500 }}>
                            {denom.label}
                          </Text>
                          <InputNumber
                            size="small"
                            min={0}
                            value={group.counts[denom.value] || 0}
                            onChange={(val) => group.onChange(denom.value, val)}
                            style={{ width: 58 }}
                          />
                          <Text type="secondary" style={{ ...moneyStyle, fontSize: 11, fontWeight: 400 }}>
                            = {currencySymbol}{((group.counts[denom.value] || 0) * denom.value).toFixed(denom.value < 1 ? 2 : 0)}
                          </Text>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>
              ))}
              <div style={{ textAlign: 'right', fontSize: 13 }}>
                Total contado{' '}
                <span style={{ ...moneyStyle, fontSize: 17 }}>{currencySymbol}{detailedTotal.toFixed(2)}</span>
              </div>

              {/* Hidden form field to maintain validation */}
              <Form.Item name="finalAmount" hidden>
                <InputNumber />
              </Form.Item>
            </div>
          ) : (
            /* Direct Input Mode */
            <Form.Item
              label="Monto contado"
              name="finalAmount"
              rules={[
                { required: true, message: 'Por favor ingrese el monto final contado' },
                { type: 'number', min: 0, message: 'El monto debe ser mayor o igual a 0' },
              ]}
              help="El dinero físico total que hay en la caja"
            >
              <InputNumber
                style={{
                  width: isMobile ? '100%' : 240,
                  fontFamily: 'ui-monospace, Consolas, monospace',
                  fontSize: 18,
                  fontWeight: 650,
                }}
                placeholder="0.00"
                min={0}
                precision={2}
                step={10}
                value={finalAmount}
                onChange={(value) => setFinalAmount(value || 0)}
                formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={(value) => parseFloat(value!.replace(/\$\s?|(,*)/g, '')) || 0}
                addonBefore={currencySymbol}
              />
            </Form.Item>
          )}

          {/* Desglose de ventas del turno: contexto, colapsado */}
          <div style={{ borderTop: '1px dashed var(--border-color)', paddingTop: 8, marginBottom: 12 }}>
            <Button
              type="link"
              size="small"
              style={{ paddingLeft: 0 }}
              onClick={() => setShowBreakdown(!showBreakdown)}
            >
              {showBreakdown ? '▾ Ocultar' : '▸ Ver'} desglose de ventas del turno
            </Button>
            {showBreakdown && (
              <div style={{ fontSize: 13 }}>
                {[
                  { label: 'Efectivo', value: cashRegister.summary?.totalSales ?? 0 },
                  { label: 'T. Crédito', value: cashRegister.summary?.totalCreditCardSales ?? 0 },
                  { label: 'T. Débito', value: cashRegister.summary?.totalDebitCardSales ?? 0 },
                  { label: 'Transferencia', value: cashRegister.summary?.totalTransferSales ?? 0 },
                  { label: 'Otros', value: cashRegister.summary?.totalOtherSales ?? 0 },
                ].map((line) => (
                  <div key={line.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                    <Text type="secondary">{line.label}</Text>
                    <span style={moneyStyle}>{currencySymbol}{line.value.toFixed(2)}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderTop: '1px solid var(--border-color)', fontWeight: 650 }}>
                  <span>Total ventas</span>
                  <span style={moneyStyle}>{currencySymbol}{(cashRegister.summary?.totalAllSales ?? 0).toFixed(2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                  <Text type="secondary">Movimientos</Text>
                  <span style={moneyStyle}>{totalMovements}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0' }}>
                  <Text type="secondary">Fondo inicial</Text>
                  <span style={moneyStyle}>
                    {currencySymbol}{(cashRegister.summary?.initialAmount ?? cashRegister.initialAmount ?? 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Observaciones: requeridas cuando hay diferencia */}
          <Form.Item
            label={hasVariance ? 'Observaciones (requeridas por la diferencia)' : 'Observaciones (opcional)'}
            name="notes"
            rules={hasVariance ? [{ required: true, message: 'Explica la diferencia antes de cerrar' }] : []}
          >
            <TextArea
              rows={2}
              placeholder={hasVariance
                ? 'Ej: cambio entregado sin registrar, billete falso retirado…'
                : 'Cualquier observación sobre el cierre'}
              maxLength={500}
              showCount
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  </>
  );
};

export default CloseCashRegisterModal;